import { LayoutStore } from "../../store/LayoutStore";
import cloneDeep from "lodash/cloneDeep";
import React, { useCallback, useMemo } from "react";
import styles from "./ManageRecordTypesSection.module.scss";
import pageCommonStyles from "../../edit-layout-page.module.scss";
import { Text } from "@whatslly/ui-components/Text";
import classnames from "classnames";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { FormattedRecordType } from "@whatslly/commons/utilClasses/FormattedRecordType";
import { modalService } from "@whatslly/ui-components/modals";
import { Select } from "@whatslly/ui-components/v2/BaseElements/Select";
import { ManageRecordTypesModal } from "@whatslly/ui-components/modals/ManageRecordTypesModal";

export const ManageRecordTypesSection: React.FC<{ layoutStore: LayoutStore }> = observer(({ layoutStore }) => {
  const { t } = useTranslation();

  const allFilteredRecordTypes = useMemo(() => {
    return layoutStore.recordTypesByEntity[layoutStore.type];
  }, [layoutStore.recordTypesByEntity, layoutStore.type]);

  const selectedRecordTypes = useMemo(() => {
    if (!allFilteredRecordTypes) return [];

    const fieldSetupKeys = Object.keys(layoutStore.fieldsClone);
    const allRecordTypesToReturn = allFilteredRecordTypes
      .filter((recordType) => fieldSetupKeys.includes(`${layoutStore.type}:${recordType.recordTypeSelector}`))
      .map((recordType) => ({ label: recordType.name, value: recordType }));

    const defaultRecordToReturn = cloneDeep(
      allFilteredRecordTypes.find((recordType) => recordType.recordTypeSelector === layoutStore.defaultRecordTypeDevName)
    );
    if (defaultRecordToReturn) {
      defaultRecordToReturn.recordTypeSelector = `${defaultRecordToReturn.recordTypeSelector}:default`;
      allRecordTypesToReturn.unshift({ label: defaultRecordToReturn.name, value: defaultRecordToReturn });
    }
    return allRecordTypesToReturn;
  }, [allFilteredRecordTypes, layoutStore.defaultRecordTypeDevName, layoutStore.fieldsClone, layoutStore.type]);

  const openManageRecordTypesModal = useCallback(async () => {
    if (!allFilteredRecordTypes) return;

    const recordTypesAsArray = Object.entries(allFilteredRecordTypes).map(([, recordType]) => recordType);
    const recordsTypesToAdd = await modalService.openModal(ManageRecordTypesModal, {
      allRecordTypes: recordTypesAsArray,
      layoutFieldsSetup: layoutStore.fieldsClone,
      type: layoutStore.type,
      defaultRecordTypeDevName: layoutStore.defaultRecordTypeDevName,
    }).promise;

    if (!recordsTypesToAdd || !Array.isArray(recordsTypesToAdd)) return;
    layoutStore.updateSelectedRecordTypes(recordsTypesToAdd);
  }, [allFilteredRecordTypes, layoutStore]);
  return (
    <div className={styles.manageRecordTypesWrapper}>
      {selectedRecordTypes.length > 0 && (
        <div>
          <div className={styles.selectRecordTypesLabelContainer}>
            <Text size={"small"} weight={"bold"} className={pageCommonStyles.selectLayoutTypeLabel}>
              {t("record_type_layout.select_record_type")}
            </Text>

            <div className={styles.editRecordTypesButton} onClick={openManageRecordTypesModal} data-testid={"edit-selected-record-types-button"}>
              {t("record_type_layout.edit")}
            </div>
          </div>
          <Select<{ label: string; value: FormattedRecordType }>
            onChange={(recordType: { label: string; value: FormattedRecordType }) => layoutStore.setSelectedRecordType(recordType.value)}
            value={
              layoutStore.selectedRecordType
                ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  { label: layoutStore.selectedRecordType.name, value: layoutStore.selectedRecordType.recordTypeSelector as any }
                : selectedRecordTypes[0]
            }
            options={selectedRecordTypes}
            name={"select_layout_type"}
            paddingInline={"none"}
            data-testid={`select-record-types-input`}
          />
        </div>
      )}
      {layoutStore.hasRecordTypes && selectedRecordTypes.length === 0 && allFilteredRecordTypes.length > 1 && (
        <div className={classnames(styles.manageRecordTypesButton)} data-testid={`manage-record-types-button`}>
          <span onClick={openManageRecordTypesModal}>{t("record_type_layout.manage_record_types")}</span>
        </div>
      )}
    </div>
  );
});
